import React, { useState } from "react";
import {
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import { DeleteOutline, ExpandMore } from "@mui/icons-material";
import { BulkAction, Task, TaskPerson, TaskStatus, taskService } from "../../../services/taskService";
import { useTaskStore } from "../TaskContext";
import { canDeleteTask } from "../taskPermissions";
import {
    apiErrorMessage,
    byBoardOrder,
    byDueThenPriority,
    dueState,
    personName,
    PRIORITY_META,
    STATUS_META,
    STATUSES,
    timeAgo,
} from "../taskUtils";
import { DueMenu, PeopleMenu, PriorityMenu, StatusMenu } from "./Pickers";
import { CompleteCircle, DueChip, LinkKind, PriorityFlag, StaffAvatar, StatusPill, TaskFacts } from "./TaskBits";

export type GroupBy = "status" | "due" | "assignee" | "none";
export type SortBy = "due" | "priority" | "title" | "updated" | "manual";

export const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
    { key: "status", label: "Status" },
    { key: "due", label: "Due date" },
    { key: "assignee", label: "Person" },
    { key: "none", label: "No grouping" },
];

export interface TaskGroup {
    key: string;
    title: string;
    color?: string;
    tasks: Task[];
}

const SORTERS: Record<SortBy, (a: Task, b: Task) => number> = {
    due: byDueThenPriority,
    priority: (a, b) => PRIORITY_META[a.priority].rank - PRIORITY_META[b.priority].rank || byDueThenPriority(a, b),
    title: (a, b) => a.title.localeCompare(b.title),
    updated: (a, b) => b.update_time.localeCompare(a.update_time),
    manual: byBoardOrder,
};

const DUE_GROUPS: { key: string; title: string; color?: string }[] = [
    { key: "overdue", title: "Overdue", color: "#b91c1c" },
    { key: "today", title: "Today", color: "#b45309" },
    { key: "soon", title: "Next 7 days" },
    { key: "later", title: "Later" },
    { key: "none", title: "No date" },
];

export function groupTasks(
    tasks: Task[],
    groupBy: GroupBy,
    sortBy: SortBy,
    today: string,
    people: Map<string, TaskPerson>,
): TaskGroup[] {
    const sorted = [...tasks].sort(SORTERS[sortBy]);
    if (groupBy === "none") return [{ key: "all", title: "All tasks", tasks: sorted }];
    if (groupBy === "status") {
        return STATUSES.map((s) => ({ key: s, title: STATUS_META[s].label, color: STATUS_META[s].color, tasks: sorted.filter((t) => t.status === s) }));
    }
    if (groupBy === "due") {
        const open = sorted.filter((t) => t.status !== "DONE");
        const groups: TaskGroup[] = DUE_GROUPS.map((g) => ({ ...g, tasks: open.filter((t) => dueState(t, today) === g.key) }));
        groups.push({ key: "done", title: "Done", color: STATUS_META.DONE.color, tasks: sorted.filter((t) => t.status === "DONE") });
        return groups;
    }
    const byPerson = new Map<string, Task[]>();
    sorted.forEach((t) => {
        const key = t.assignee_user_id || "none";
        byPerson.set(key, [...(byPerson.get(key) || []), t]);
    });
    return Array.from(byPerson.entries())
        .map(([key, list]) => ({
            key,
            title: key === "none" ? "Unassigned" : people.has(key) ? personName(people.get(key)) : "Former staff member",
            tasks: list,
        }))
        .sort((a, b) => (a.key === "none" ? 1 : b.key === "none" ? -1 : a.title.localeCompare(b.title)));
}

/** Rows in the order shown — for ↑ ↓ and stepping through tasks. */
export const visibleRows = (groups: TaskGroup[], collapsed: Set<string>) =>
    groups.filter((g) => !collapsed.has(g.key)).flatMap((g) => g.tasks);

type CellMenu = { kind: "status" | "assignee" | "due" | "priority"; task: Task; anchor: HTMLElement };
type BulkMenu = { kind: "status" | "assignee" | "due" | "priority"; anchor: HTMLElement };

interface TaskListProps {
    groups: TaskGroup[];
    groupBy: GroupBy;
    sortBy: SortBy;
    onSort: (sort: SortBy) => void;
    collapsed: Set<string>;
    onToggleGroup: (key: string) => void;
    activeId: string | null;
    onOpen: (taskId: string) => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
}

export const TaskList: React.FC<TaskListProps> = ({ groups, groupBy, sortBy, onSort, collapsed, onToggleGroup, activeId, onOpen, onOpenLink }) => {
    const store = useTaskStore();
    const { today, peopleById, viewer, rightsFor, patch, reload, toast, announce } = store;
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [cellMenu, setCellMenu] = useState<CellMenu | null>(null);
    const [bulkMenu, setBulkMenu] = useState<BulkMenu | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const shown = visibleRows(groups, collapsed);
    const shownIds = new Set(shown.map((t) => t.task_id));
    // Forget selections that left the view (filtered out, deleted).
    const picked = Array.from(selected).filter((id) => shownIds.has(id));
    const allPicked = shown.length > 0 && picked.length === shown.length;

    const toggle = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const runBulk = async (action: BulkAction) => {
        if (!picked.length) return;
        setBusy(true);
        try {
            const res = await taskService.bulk(picked, action);
            await reload(true);
            announce();
            toast({
                message: action.action === "delete" ? `Deleted ${res.updated} task${res.updated === 1 ? "" : "s"}.` : `Updated ${res.updated} task${res.updated === 1 ? "" : "s"}.`,
                severity: "success",
            });
            setSelected(new Set());
        } catch (err) {
            toast({ message: apiErrorMessage(err, "Couldn't update those tasks."), severity: "error" });
        } finally {
            setBusy(false);
        }
    };

    const openCell = (kind: CellMenu["kind"], task: Task) => (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (!rightsFor(task).canEdit) return;
        setCellMenu({ kind, task, anchor: e.currentTarget });
    };

    const header = (label: string, key: SortBy | null, width?: number | string) => (
        <TableCell sx={{ width, whiteSpace: "nowrap", fontWeight: 600, color: "text.secondary", fontSize: 12.5, py: 1 }}>
            {key ? (
                <TableSortLabel active={sortBy === key} direction="asc" onClick={() => onSort(sortBy === key ? "manual" : key)}>
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    );

    return (
        <Box>
            {/* Floats at the bottom, so ticking a row never shifts the table
                (the next click would land on the wrong row). */}
            {picked.length > 0 && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: (t) => t.zIndex.drawer - 1, // under menus, dialogs and the task drawer
                        width: "max-content",
                        maxWidth: "calc(100vw - 32px)",
                        px: 2,
                        py: 1,
                        borderRadius: 3,
                        bgcolor: "#18383E",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                        {picked.length} selected
                    </Typography>
                    {(["assignee", "status", "due", "priority"] as const).map((k) => (
                        <Button
                            key={k}
                            size="small"
                            disabled={busy}
                            onClick={(e) => setBulkMenu({ kind: k, anchor: e.currentTarget })}
                            sx={{ color: "#fff", textTransform: "none", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.18)" } }}
                        >
                            {k === "assignee" ? "Assign" : k === "status" ? "Move" : k === "due" ? "Reschedule" : "Priority"}
                        </Button>
                    ))}
                    {canDeleteTask(viewer) && (
                        <Button
                            size="small"
                            disabled={busy}
                            startIcon={<DeleteOutline />}
                            onClick={() => setConfirmDelete(true)}
                            sx={{ color: "#fecaca", textTransform: "none" }}
                        >
                            Delete
                        </Button>
                    )}
                    <Button size="small" onClick={() => setSelected(new Set())} sx={{ color: "#fff", textTransform: "none", opacity: 0.8, ml: 1 }}>
                        Clear
                    </Button>
                </Paper>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                <Table size="small" sx={{ minWidth: 860, "& td, & th": { borderColor: "#eef1f0" } }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#f8faf9" }}>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allPicked}
                                    indeterminate={picked.length > 0 && !allPicked}
                                    onChange={() => setSelected(allPicked ? new Set() : new Set(shown.map((t) => t.task_id)))}
                                    inputProps={{ "aria-label": "Select all" }}
                                />
                            </TableCell>
                            {header("Task", "title")}
                            {header("Assignee", null, 170)}
                            {header("Status", null, 140)}
                            {header("Due", "due", 140)}
                            {header("Priority", "priority", 110)}
                            {header("Updated", "updated", 100)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => {
                            if (groupBy !== "none" && group.tasks.length === 0) return null;
                            const isCollapsed = collapsed.has(group.key);
                            return (
                                <React.Fragment key={group.key}>
                                    {groupBy !== "none" && (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ py: 0.75, bgcolor: "#fcfdfd" }}>
                                                <ButtonBase onClick={() => onToggleGroup(group.key)} sx={{ gap: 0.75, borderRadius: 1, px: 0.5 }}>
                                                    <ExpandMore
                                                        sx={{ fontSize: 18, color: "text.secondary", transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 150ms" }}
                                                    />
                                                    {groupBy === "assignee" && (
                                                        <StaffAvatar person={peopleById.get(group.key)} userId={group.key === "none" ? null : group.key} size={20} tooltip={false} />
                                                    )}
                                                    {group.color && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: group.color }} />}
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {group.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        {group.tasks.length}
                                                    </Typography>
                                                </ButtonBase>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isCollapsed &&
                                        group.tasks.map((task) => (
                                            <ListRow
                                                key={task.task_id}
                                                task={task}
                                                today={today}
                                                people={peopleById}
                                                active={task.task_id === activeId}
                                                selected={selected.has(task.task_id)}
                                                canEdit={rightsFor(task).canEdit}
                                                onSelect={() => toggle(task.task_id)}
                                                onOpen={() => onOpen(task.task_id)}
                                                onOpenLink={onOpenLink}
                                                onCell={openCell}
                                                onComplete={() => patch(task, { status: task.status === "DONE" ? "TODO" : "DONE" })}
                                            />
                                        ))}
                                </React.Fragment>
                            );
                        })}
                        {shown.length === 0 && groups.every((g) => g.tasks.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                                    No tasks match.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Single-row edits */}
            {cellMenu && (
                <>
                    <StatusMenu
                        anchorEl={cellMenu.kind === "status" ? cellMenu.anchor : null}
                        onClose={() => setCellMenu(null)}
                        value={cellMenu.task.status}
                        onSelect={(status: TaskStatus) => patch(cellMenu.task, { status })}
                    />
                    <PeopleMenu
                        anchorEl={cellMenu.kind === "assignee" ? cellMenu.anchor : null}
                        onClose={() => setCellMenu(null)}
                        value={cellMenu.task.assignee_user_id}
                        onSelect={(id) => id !== cellMenu.task.assignee_user_id && patch(cellMenu.task, { assignee_user_id: id })}
                    />
                    <DueMenu
                        anchorEl={cellMenu.kind === "due" ? cellMenu.anchor : null}
                        onClose={() => setCellMenu(null)}
                        date={cellMenu.task.due_date}
                        time={cellMenu.task.due_time}
                        today={today}
                        onSelect={(due_date, due_time) => patch(cellMenu.task, { due_date, due_time })}
                    />
                    <PriorityMenu
                        anchorEl={cellMenu.kind === "priority" ? cellMenu.anchor : null}
                        onClose={() => setCellMenu(null)}
                        value={cellMenu.task.priority}
                        onSelect={(priority) => patch(cellMenu.task, { priority })}
                    />
                </>
            )}

            {/* Bulk edits */}
            <StatusMenu
                anchorEl={bulkMenu?.kind === "status" ? bulkMenu.anchor : null}
                onClose={() => setBulkMenu(null)}
                value={"" as TaskStatus}
                onSelect={(status) => runBulk({ action: "status", status })}
            />
            <PeopleMenu
                anchorEl={bulkMenu?.kind === "assignee" ? bulkMenu.anchor : null}
                onClose={() => setBulkMenu(null)}
                value={null}
                onSelect={(id) => runBulk({ action: "assign", assignee_user_id: id })}
            />
            <DueMenu
                anchorEl={bulkMenu?.kind === "due" ? bulkMenu.anchor : null}
                onClose={() => setBulkMenu(null)}
                date={null}
                time={null}
                today={today}
                onSelect={(due_date) => runBulk({ action: "due", due_date })}
            />
            <PriorityMenu
                anchorEl={bulkMenu?.kind === "priority" ? bulkMenu.anchor : null}
                onClose={() => setBulkMenu(null)}
                value={"" as any}
                onSelect={(priority) => runBulk({ action: "priority", priority })}
            />

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Delete {picked.length} task{picked.length === 1 ? "" : "s"}?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        They'll disappear for everyone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        autoFocus
                        onClick={() => {
                            setConfirmDelete(false);
                            runBulk({ action: "delete" });
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

interface ListRowProps {
    task: Task;
    today: string;
    people: Map<string, TaskPerson>;
    active: boolean;
    selected: boolean;
    canEdit: boolean;
    onSelect: () => void;
    onOpen: () => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
    onCell: (kind: CellMenu["kind"], task: Task) => (e: React.MouseEvent<HTMLElement>) => void;
    onComplete: () => void;
}

const cellButton = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 0.75,
    py: 0.4,
    mx: -0.75,
    borderRadius: 1.25,
    maxWidth: "100%",
    "&:hover": { bgcolor: "action.hover" },
};

const ListRow: React.FC<ListRowProps> = ({ task, today, people, active, selected, canEdit, onSelect, onOpen, onOpenLink, onCell, onComplete }) => {
    const ref = React.useRef<HTMLTableRowElement>(null);
    React.useEffect(() => {
        if (active) ref.current?.scrollIntoView({ block: "nearest" });
    }, [active]);
    const assignee = task.assignee_user_id ? people.get(task.assignee_user_id) : undefined;
    const done = task.status === "DONE";
    // Facts already shown in their own columns stay out of the title cell.
    const titleFacts = { ...task, due_date: null, priority: "NORMAL" as const };

    return (
        <TableRow
            ref={ref}
            hover
            selected={selected}
            onClick={onOpen}
            sx={{
                cursor: "pointer",
                "& td": { py: 0.9 },
                ...(active ? { bgcolor: "#eef4f3", boxShadow: "inset 3px 0 0 #18383E" } : {}),
            }}
        >
            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                <Checkbox size="small" checked={selected} onChange={onSelect} inputProps={{ "aria-label": `Select ${task.title}` }} />
            </TableCell>
            <TableCell>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={{ pt: 0.15 }}>
                        <CompleteCircle done={done} disabled={!canEdit} onToggle={onComplete} size={18} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 550, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}>
                            {task.title}
                        </Typography>
                        {(task.checklist_total > 0 || task.comment_count > 0 || task.labels.length > 0 || task.repeat_rule || task.order_id || task.customer_id || task.product_id) && (
                            <Box sx={{ mt: 0.25 }}>
                                <TaskFacts task={titleFacts} today={today} hideDue onOpenLink={onOpenLink} />
                            </Box>
                        )}
                    </Box>
                </Stack>
            </TableCell>
            <TableCell>
                <ButtonBase onClick={onCell("assignee", task)} disabled={!canEdit} sx={cellButton}>
                    <StaffAvatar person={assignee} userId={task.assignee_user_id} size={22} tooltip={false} />
                    <Typography variant="body2" noWrap color={assignee ? "text.primary" : "text.secondary"}>
                        {assignee ? personName(assignee) : "Unassigned"}
                    </Typography>
                </ButtonBase>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
                <StatusPill status={task.status} onClick={canEdit ? onCell("status", task) : undefined} />
            </TableCell>
            <TableCell>
                <ButtonBase onClick={onCell("due", task)} disabled={!canEdit} sx={cellButton}>
                    <DueChip task={task} today={today} showEmpty />
                </ButtonBase>
            </TableCell>
            <TableCell>
                <ButtonBase onClick={onCell("priority", task)} disabled={!canEdit} sx={cellButton}>
                    <PriorityFlag priority={task.priority} withLabel />
                </ButtonBase>
            </TableCell>
            <TableCell sx={{ color: "text.secondary", fontSize: 12.5, whiteSpace: "nowrap" }}>{timeAgo(task.update_time)}</TableCell>
        </TableRow>
    );
};
